import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { useParams, useHistory, Redirect } from "react-router-dom";

import { SettingsContext, UserContext } from '../contexts/index.jsx';
import { Loading } from "../components";
import StatePanel from "../components/ui/StatePanel";
import { Button, ButtonGroup } from "../components/ui";
import { requireResponse } from "../core/errors/requireResponse";
import { RCListingForm } from "../forms";
import ContractsSection from "../listingFlows/ContractsSection.jsx";
import {
  FlowSection,
  AssignPersonPicker,
  LinkedRequestSlot,
  IUserIcon,
} from "../listingFlows/FlowKit.jsx";
import {
  fetchPurchasingAgreement,
  fetchConsignmentAgreement,
  createDoneSellRequest,
  attachListingToRequest,
  fetchRequest,
} from "../listingFlows/intakeApi";
import useAutoContractDocs from "../listingFlows/useAutoContractDocs";

const personName = (u) =>
  u?.name || [u?.first_name, u?.last_name].filter(Boolean).join(" ") || u?.email || "";

/**
 * Edit Listing.
 *
 * Uses the same section overlay the purpose-built create flows use, so a listing
 * reopens looking like the flow that made it — whichever flow that was, and
 * including listings created before those flows existed:
 *
 *   ASSIGN SELLER / ASSIGN CONSIGNOR — the intake section, titled from the live
 *     "Owned By" value (Consignment ⇒ consignor). Optional and skippable: plenty
 *     of listings have no one recorded, and editing one is not the moment to
 *     force the question.
 *   INTERNAL DETAILS — the linked Sell Request above the supplier picker, and
 *     the In Repair toggle under Cost (both from the form itself). A listing
 *     with no request behind it can be given one here — select an existing one
 *     or create it — and either action persists immediately, because unlike the
 *     create flows the listing already exists.
 *   CONTRACTS — Purchase Order / Invoices / Agreements, built from the saved
 *     listing, with the agreements pulled from the linked request.
 *
 * No LISTING STATUS section: editing keeps the normal Save & Close / Update
 * Product footer.
 */
const ListingEdit = () => {
  const history = useHistory();
  const { listingId } = useParams();
  const [listing, setListing] = useState();
  const { user } = useContext(UserContext);
  const settings = useContext(SettingsContext);

  // Intake section (rendered above the form, outside Formik — same slot the
  // create flows use).
  const [intakeOpen, setIntakeOpen] = useState(false);
  // Three states, and the difference matters: `undefined` = untouched (show the
  // saved supplier), an object = reassigned, `null` = explicitly unassigned via
  // the × (which must actually empty the field, not fall back to the old name).
  const [person, setPerson] = useState(undefined);
  // Live "Owned By", reported back out of Formik by the form. Seeded from the
  // saved listing so the heading is right on first paint, before the report.
  const [ownedBy, setOwnedBy] = useState(null);
  const [agreements, setAgreements] = useState({});
  // The first load failed (5xx / dropped connection / 4xx): show a retry
  // instead of the loading spinner forever.
  const [loadFailed, setLoadFailed] = useState(false);

  const loadListing = useCallback(async ({ first = false } = {}) => {
    const { data } = requireResponse(
      await axios.get(`/api/listings/${listingId}`, { params: { mark_reviewed: 1 } })
    );
    setListing(data);
    if (first) {
      setOwnedBy(data?.internal_product ?? null);
      // Open the intake section only when there is nobody recorded — that is
      // the case worth a prompt. A listing that already names its seller shows
      // the name in the collapsed header instead. Only on first load: a reload
      // after linking a request must not reopen a section the rep has closed.
      setIntakeOpen(!data?.supplier);
    }
  }, [listingId]);

  const loadFirst = useCallback(() => {
    setLoadFailed(false);
    loadListing({ first: true }).catch(() => setLoadFailed(true));
  }, [loadListing]);

  useEffect(() => {
    loadFirst();
  }, [loadFirst]);

  if (!listing && loadFailed) {
    return (
      <StatePanel
        state="error"
        title="Couldn't load this listing"
        description="Something went wrong on our side — try again."
        onRetry={loadFirst}
      />
    );
  }

  if (!listing) {
    return (
      <div style={{ padding: "60px 0", display: "flex", justifyContent: "center" }}>
        <Loading type="loading" showText={false} />
      </div>
    );
  }

  if (!user.is_admin && listing.user_id !== user.id) {
    return <Redirect to="/error/403" />;
  }

  const afterSubmit = (saved) =>
    history.push("/product/" + saved.id + `?${settings.iframeQueryParams}`);

  return (
    <>
      <Helmet>
        <title>Edit Listing — Retail Circle</title>
        <meta name="robots" content="noindex, nofollow, noarchive" />
      </Helmet>

      <ListingEditForm
        listing={listing}
        reloadListing={loadListing}
        afterSubmit={afterSubmit}
        intakeOpen={intakeOpen}
        setIntakeOpen={setIntakeOpen}
        person={person}
        setPerson={setPerson}
        ownedBy={ownedBy}
        setOwnedBy={setOwnedBy}
        agreements={agreements}
        setAgreements={setAgreements}
      />
    </>
  );
};

// Split out so the overlay can be memoised against the loaded listing without
// the hooks above running before it exists.
const ListingEditForm = ({
  listing,
  reloadListing,
  afterSubmit,
  intakeOpen,
  setIntakeOpen,
  person,
  setPerson,
  ownedBy,
  setOwnedBy,
  agreements,
  setAgreements,
}) => {
  const linkedRequest = listing.source_sell_request;
  const requestId = listing.source_sell_request_id;
  // The product currently selected in the form (it can be changed here), needed
  // to raise a Sell Request. Seeded from the saved listing.
  const [product, setProduct] = useState(listing.product ?? null);

  // "Consignment" (internal_product = exclusive) means the store never bought
  // the watch — the counterparty is a consignor, not a seller. Everything else
  // (store-owned stock, merchant listings) is a seller.
  const isConsignment = (ownedBy ?? listing.internal_product) === "exclusive";
  const roleLabel = isConsignment ? "Consignor" : "Seller";

  const touched = person !== undefined;
  const savedPerson = listing.supplier
    ? { id: listing.supplier_id, name: listing.supplier }
    : null;
  // Who is recorded now: a fresh pick (or an explicit clear) wins over the saved
  // supplier name.
  const shown = touched ? person : savedPerson;
  const assigned = shown?.name || null;

  const loadAgreement = async (key) => {
    if (!requestId) return;
    const fetcher = key === "consignment" ? fetchConsignmentAgreement : fetchPurchasingAgreement;
    setAgreements((a) => ({ ...a, [key]: { loading: true } }));
    try {
      const { file_id, signed_url } = await fetcher(requestId);
      setAgreements((a) => ({ ...a, [key]: { url: signed_url || file_id } }));
    } catch (e) {
      // 403 = the store's plan does not include purchase/consignment agreements
      // (the endpoints sit behind feature:purchase_consignment_agreements), which
      // is worth saying plainly rather than reporting as a failure.
      const denied = e?.response?.status === 403;
      setAgreements((a) => ({
        ...a,
        [key]: {
          error: denied
            ? "Purchase & consignment agreements are not enabled on this plan."
            : `Could not fetch the ${key} agreement from the linked Sell Request.`,
        },
      }));
    }
  };

  const AGREEMENT_KEYS = [
    { key: "purchasing",  label: "Purchasing Agreement",  sentAt: linkedRequest?.purchasing_agreement_sent_at },
    { key: "consignment", label: "Consignment Agreement", sentAt: linkedRequest?.consignment_agreement_sent_at },
  ];

  // The listing embeds only a request SUMMARY — the auto-docs hook needs the
  // full payload (last_quote, xero_connection_id, PO file fields), so re-read
  // the linked request whenever the link changes. Read-only; a failure just
  // leaves the documents on their manual buttons.
  const [fullRequest, setFullRequest] = useState(null);
  useEffect(() => {
    let stale = false;
    if (!requestId) {
      setFullRequest(null);
      return undefined;
    }
    fetchRequest(requestId)
      .then((r) => { if (!stale) setFullRequest(r); })
      .catch(() => { if (!stale) setFullRequest(null); });
    return () => { stale = true; };
  }, [requestId]);

  // Auto-surface the linked request's documents (see useAutoContractDocs):
  // already-generated agreements fetch immediately; the agreement relevant to
  // this listing's Owned By and the Xero PO auto-generate when the request has
  // a quote and they don't exist yet.
  const [poAutoNote, setPoAutoNote] = useState(null);
  useAutoContractDocs({
    request: fullRequest,
    kind: isConsignment ? "consignment" : "purchasing",
    agreements,
    loadAgreement,
    poPresent: !!(listing.purchase_order_file_url ?? listing.purchase_order_file_id),
    onPo: ({ ok, message }) => {
      if (ok) {
        setPoAutoNote(null);
        // GET /listings/{id} resolves the PO live from the linked request —
        // re-read so the Purchase Order tab shows the fresh document.
        reloadListing();
      } else {
        setPoAutoNote(message);
      }
    },
  });

  // ── Linking a request from the EDIT screen ─────────────────────────────────
  // Unlike the create flows, the listing already exists here — so both actions
  // persist immediately (POST /api/requests/{id}/attach-listing) and the listing
  // is re-read so the slot, the Purchase Order tab and the agreements all come
  // from the request that is now linked.
  const linkExisting = async (req) => {
    await attachListingToRequest(req.id, listing.id);
    setAgreements({});
    await reloadListing();
  };

  const createAndLink = async () => {
    const client = shown;
    const created = await createDoneSellRequest({
      clientId: client.id,
      productId: product.id,
      listingId: listing.id,
      isConsignment,
    });
    setAgreements({});
    await reloadListing();
    return created;
  };

  // Both prerequisites name themselves, so the button can say why it is off.
  const createBlockedNote =
    !shown?.id
      ? `Assign the ${roleLabel.toLowerCase()} above first — the Sell Request is raised in their name.`
      : !product?.id
        ? "This listing has no product selected, so a Sell Request cannot be created."
        : null;

  const flow = useMemo(() => ({
    // No `title` — the form falls back to "Edit Listing".
    // No `renderStatusSection` — the normal footer stays.
    reportFields: ["internal_product"],
    onReport: (v) => setOwnedBy(v.internal_product),
    // The product can be changed on this screen too, and a Sell Request raised
    // here must carry the product the rep is actually looking at.
    onProductChange: (picked) => {
      if ((picked?.id ?? null) !== (product?.id ?? null)) setProduct(picked);
    },

    // A picked person is ENFORCED rather than prefilled: the listing usually
    // already has a supplier name, and a soft prefill only fills empty fields —
    // it could never change one. An explicit unassign goes through `clear`,
    // the one bucket allowed to write an empty value.
    enforce: person?.id
      ? { supplier: person.name, supplier_id: person.id }
      : undefined,
    clear: person === null ? ["supplier", "supplier_id"] : undefined,

    topSections: (
      <FlowSection
        icon={<IUserIcon />}
        label={`Assign ${roleLabel}`}
        isOpen={intakeOpen}
        onToggle={() => setIntakeOpen((o) => !o)}
        summary={assigned}
      >
        <AssignPersonPicker
          label={roleLabel}
          suggestions={[]}
          suggestionsNote={
            assigned
              ? `Recorded as ${assigned}. Search to reassign, or add a new user.`
              : `Nobody is recorded as the ${roleLabel.toLowerCase()} for this listing — optional, and safe to skip.`
          }
          value={shown}
          onChange={(picked) => setPerson(picked ?? null)}
          createTitle={`New ${roleLabel}`}
        />
        {/* Optional by design — skipping just collapses the section. Nothing is
            written until the listing is saved, so there is nothing to undo. */}
        <div className="rc-flow__source-actions">
          <span className="rc-flow__skip-note">
            This section is optional — the rest of the listing saves either way.
          </span>
          <ButtonGroup align="end">
            <Button variant="ghost" data-btn="intake-cancel" onClick={() => setIntakeOpen(false)}>
              Skip
            </Button>
          </ButtonGroup>
        </div>
      </FlowSection>
    ),

    // Above the supplier picker, not instead of it: the picker is still the only
    // way to correct a supplier name, and plenty of listings have no request
    // behind them. When one IS linked the card is read-only (unlinking belongs
    // to the request flow, which carries the deposit guardrail); when none is,
    // the same Select / Create actions the create flows offer are available and
    // persist straight away.
    linkedRequestSlot: linkedRequest ? (
      <LinkedRequestSlot
        readOnly
        selected={{
          id: linkedRequest.id,
          status: linkedRequest.status,
          product: listing.product,
          client: { first_name: linkedRequest.client_name },
        }}
      />
    ) : (
      <LinkedRequestSlot
        selected={null}
        onSelect={linkExisting}
        onCreate={createAndLink}
        createBlockedNote={createBlockedNote}
      />
    ),

    renderContractsSection: ({ openSection, toggleSection }) => (
      <ContractsSection
        openSection={openSection}
        toggleSection={toggleSection}
        poUrl={listing.purchase_order_file_url ?? listing.purchase_order_file_id ?? null}
        poEmptyNote={poAutoNote ?? undefined}
        // Agreements come from the LINKED request. Already-generated ones (and
        // the one relevant to this listing when the request carries a quote)
        // are auto-fetched by useAutoContractDocs above; the other one stays on
        // its manual button because generating stamps *_agreement_sent_at.
        // Manual upload stays available either way. Nothing is emailed.
        agreementSlots={AGREEMENT_KEYS.map(({ key, label, sentAt }) => ({
          key,
          label: sentAt ? `${label} (sent)` : label,
          url: agreements[key]?.url,
          loading: agreements[key]?.loading,
          error: agreements[key]?.error,
          onLoad: requestId ? () => loadAgreement(key) : null,
        }))}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [listing, person, intakeOpen, roleLabel, assigned, agreements, requestId, product, createBlockedNote, poAutoNote]);

  return <RCListingForm listing={listing} editableProduct={true} afterSubmit={afterSubmit} flow={flow} />;
};

export default ListingEdit;
